import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Table, Segmented, Tag, Button, Row, Col, Typography, Input, Spin, message, Empty } from 'antd';
import { FilePdfOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCustomerInquiryReport } from './GeneralService';
import { errHandler } from '../../helper/helper';

const { Text } = Typography;

const formatDateOfBirth = (value) => {
    if (!value) return '-';
    const parsed = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'D/M/YYYY', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : String(value);
};

const accountColumns = [
    {
        title: 'Acc Number',
        dataIndex: 'accountNumber',
        key: 'accountNumber',
        width: 140,
        align: 'center',
    },
    {
        title: 'Account Type',
        dataIndex: 'accountType',
        key: 'accountType',
        align: 'left',
    },
    {
        title: 'Branch',
        dataIndex: 'branch',
        key: 'branch',
        align: 'left',
    },
    {
        title: 'Currency',
        dataIndex: 'currency',
        key: 'currency',
        width: 90,
        align: 'center',
    },
    {
        title: 'Available Balance',
        dataIndex: 'availableBalance',
        key: 'availableBalance',
        render: (val) => (val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'),
        align: 'right',
    },
    {
        title: 'Ledger Balance',
        dataIndex: 'ledgerBalance',
        key: 'ledgerBalance',
        render: (val) => (val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'),
        align: 'right',
    },
    {
        title: 'Account Status',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (status) => {
            const s = (status || '').toUpperCase();
            let color = 'default';
            if (s === 'ACTIVE') color = 'success';
            else if (s === 'DORMANT' || s === 'INACTIVE') color = 'warning';
            else if (s === 'CLOSED') color = 'error';
            return <Tag color={color}>{status || '-'}</Tag>;
        },
        align: 'center',
    },
    {
        title: 'Opened Date',
        dataIndex: 'openedDate',
        key: 'openedDate',
        width: 120,
        render: (date) => (date ? moment(date).format('YYYY-MM-DD') : '-'),
        align: 'center',
    },
];

const loanColumns = [
    {
        title: 'Loan No',
        dataIndex: 'loanNumber',
        key: 'loanNumber',
        width: 140,
        align: 'center',
    },
    {
        title: 'Loan Type',
        dataIndex: 'loanType',
        key: 'loanType',
        align: 'left',
    },
    {
        title: 'Branch',
        dataIndex: 'branch',
        key: 'branch',
        align: 'left',
    },
    {
        title: 'Loan Amount',
        dataIndex: 'loanAmount',
        key: 'loanAmount',
        render: (val) => (val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'),
        align: 'right',
    },
    {
        title: 'Outstanding Balance',
        dataIndex: 'outstandingBalance',
        key: 'outstandingBalance',
        render: (val) => (
            <span style={{ color: '#cf1322', fontWeight: 500 }}>
                {val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </span>
        ),
        align: 'right',
    },
    {
        title: 'Installment Amount',
        dataIndex: 'installmentAmount',
        key: 'installmentAmount',
        render: (val) => (val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'),
        align: 'right',
    },
    {
        title: 'Interest Rate (%)',
        dataIndex: 'interestRate',
        key: 'interestRate',
        width: 130,
        render: (val) => (val !== undefined && val !== null ? `${Number(val).toFixed(2)}%` : '-'),
        align: 'right',
    },
    {
        title: 'Loan Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => {
            const s = (status || '').toUpperCase();
            let color = 'default';
            if (s === 'ACTIVE' || s === 'SETTLED' || s === 'RECOVERED') color = 'success';
            else if (s === 'PENDING' || s === 'DELAYED') color = 'warning';
            else if (s === 'DEFAULT' || s === 'CLOSED' || s === 'EXPIRED') color = 'error';
            return <Tag color={color}>{status || '-'}</Tag>;
        },
        align: 'center',
    },
    {
        title: 'Start Date',
        dataIndex: 'startDate',
        key: 'startDate',
        width: 120,
        render: (date) => (date ? moment(date).format('YYYY-MM-DD') : '-'),
        align: 'center',
    },
];

const groupColumns = [
    {
        title: 'Group ID',
        dataIndex: 'groupId',
        key: 'groupId',
        width: 120,
        align: 'center',
    },
    {
        title: 'Group Name',
        dataIndex: 'groupName',
        key: 'groupName',
        align: 'left',
    },
    {
        title: 'Group Type',
        dataIndex: 'groupType',
        key: 'groupType',
        align: 'left',
    },
    {
        title: 'Branch',
        dataIndex: 'branch',
        key: 'branch',
        align: 'left',
    },
    {
        title: 'Member Number',
        dataIndex: 'memberNumber',
        key: 'memberNumber',
        width: 130,
        align: 'center',
    },
    {
        title: 'Group Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => {
            const s = (status || '').toUpperCase();
            let color = 'default';
            if (s === 'ACTIVE') color = 'success';
            else if (s === 'INACTIVE') color = 'warning';
            return <Tag color={color}>{status || '-'}</Tag>;
        },
        align: 'center',
    },
    {
        title: 'Joined Date',
        dataIndex: 'joinedDate',
        key: 'joinedDate',
        width: 120,
        render: (date) => (date ? moment(date).format('YYYY-MM-DD') : '-'),
        align: 'center',
    },
];

const CustomerInquirySummaryTable = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { customerData: initialCustomerData, nic: initialNic } = location.state || {};
    const [customerData, setCustomerData] = useState(initialCustomerData || {});
    const [nic, setNic] = useState((initialNic === 'undefined' || initialNic === 'null') ? '' : (initialNic || initialCustomerData?.customer?.nic || ''));
    const [searchNic, setSearchNic] = useState(nic);
    const [loading, setLoading] = useState(false);

    const [viewMode, setViewMode] = useState('All');
    const [accountsPageSize, setAccountsPageSize] = useState(15);
    const [loansPageSize, setLoansPageSize] = useState(15);
    const [groupsPageSize, setGroupsPageSize] = useState(15);

    const customer = customerData?.customer || {};
    const accounts = customerData?.accounts || {};
    const loans = customerData?.loans || {};
    const groups = customerData?.groups || [];

    const accountsList = accounts?.items || [];
    const loansList = loans?.items || [];
    const groupsList = Array.isArray(groups) ? groups : [];

    const hasCustomerDetails = Boolean(customer?.customerId || customer?.name || customer?.nic);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const queryNic = searchParams.get('nic');
        if (queryNic && !hasCustomerDetails) {
            setSearchNic(queryNic);
            executeSearch(queryNic);
        }
    }, [location.search]);

    const executeSearch = async (nicToSearch) => {
        const trimmed = (nicToSearch || '').trim();
        if (!trimmed) {
            message.warning('Please enter a NIC number.');
            return;
        }

        setLoading(true);
        try {
            const response = await getCustomerInquiryReport({ nic: trimmed });
            let resultData = null;
            if (response && (response.customer || response.accounts || response.loans)) {
                resultData = response;
            } else if (response && response.data) {
                resultData = response.data;
            }

            if (response && response.status && response.status !== 200) {
                message.error(response.message || 'No customer found for the provided NIC number.');
                return;
            }

            if (!resultData || !resultData.customer) {
                message.error('No customer found for the provided NIC number.');
                return;
            }

            setCustomerData(resultData);
            setNic(trimmed);
            message.success(`Customer details loaded for ${resultData.customer.name || trimmed}`);
        } catch (err) {
            console.error('Error fetching customer inquiry:', err);
            if (err === 'access denied' || err?.status === 401 || err?.status === 403) {
                message.error('Access denied. You do not have permission to view customer inquiry.');
            } else if (err?.data?.message) {
                message.error(err.data.message);
            } else {
                errHandler(err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        executeSearch(searchNic);
    };

    const handleGeneratePDF = () => {
        const doc = new jsPDF('landscape');
        let startY = 15;

        // Header Title
        doc.setFontSize(16);
        doc.setTextColor(41, 128, 185);
        doc.text(`Customer Inquiry Summary - ${nic || '-'}`, 14, startY);
        startY += 8;

        const addressText = customer.address ? String(customer.address).trim() : '-';
        const isAddressLong = addressText.length > 28;

        const customerInfoBody = isAddressLong
            ? [
                  [
                      { content: 'Customer ID:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.customerId || '-') },
                      { content: 'Customer Name:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.name || '-') },
                      { content: 'NIC Number:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.nic || nic || '-') },
                      { content: 'Date of Birth:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: formatDateOfBirth(customer.dateOfBirth) },
                  ],
                  [
                      { content: 'Mobile Number:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.mobileNumber || '-') },
                      { content: 'Customer Status:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.status || 'Active') },
                      { content: 'Customer Type:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.customerType || 'Individual'), colSpan: 3 },
                  ],
                  [
                      { content: 'Address:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: addressText, colSpan: 7 },
                  ],
              ]
            : [
                  [
                      { content: 'Customer ID:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.customerId || '-') },
                      { content: 'Customer Name:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.name || '-') },
                      { content: 'NIC Number:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.nic || nic || '-') },
                      { content: 'Date of Birth:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: formatDateOfBirth(customer.dateOfBirth) },
                  ],
                  [
                      { content: 'Mobile Number:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.mobileNumber || '-') },
                      { content: 'Customer Status:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.status || 'Active') },
                      { content: 'Customer Type:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: String(customer.customerType || 'Individual') },
                      { content: 'Address:', styles: { fontStyle: 'bold', fillColor: [245, 247, 250] } },
                      { content: addressText },
                  ],
              ];

        autoTable(doc, {
            body: customerInfoBody,
            startY: startY,
            theme: 'grid',
            styles: {
                fontSize: 8.5,
                cellPadding: 2.2,
                lineColor: [210, 215, 225],
                lineWidth: 0.2,
                textColor: [40, 40, 40],
                overflow: 'linebreak',
            },
            columnStyles: {
                0: { cellWidth: 26 },
                1: { cellWidth: 36 },
                2: { cellWidth: 26 },
                3: { cellWidth: 44 },
                4: { cellWidth: 24 },
                5: { cellWidth: 34 },
                6: { cellWidth: 26 },
                7: { cellWidth: 53 },
            },
            margin: { left: 14, right: 14 },
        });

        startY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : startY) + 8;

        const renderTableSection = (title, columns, data) => {
            if (startY > 160) {
                doc.addPage();
                startY = 15;
            }

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(41, 128, 185);
            doc.text(title, 14, startY);
            startY += 4;

            const tableHeaders = columns.map(col => col.title);
            const tableRows = (data || []).map(row => {
                return columns.map(col => {
                    const val = row[col.dataIndex];
                    if (col.render) {
                        if (col.dataIndex === 'openedDate' || col.dataIndex === 'startDate' || col.dataIndex === 'maturityDate' || col.dataIndex === 'nextPaymentDate' || col.dataIndex === 'joinedDate') {
                            return val ? moment(val).format('YYYY-MM-DD') : '-';
                        }
                        if (col.dataIndex.toLowerCase().includes('balance') || col.dataIndex.toLowerCase().includes('amount')) {
                            return val !== undefined && val !== null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
                        }
                    }
                    return val !== undefined && val !== null ? String(val) : '-';
                });
            });

            autoTable(doc, {
                head: [tableHeaders],
                body: tableRows.length > 0 ? tableRows : [[{ content: 'No records found', colSpan: tableHeaders.length, styles: { halign: 'center' } }]],
                startY: startY,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                theme: 'striped',
                margin: { left: 14, right: 14 },
                didDrawPage: (data) => {
                    startY = data.cursor.y;
                },
            });

            startY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : startY) + 8;
        };

        // Render Tables in PDF
        renderTableSection('Customer Accounts', accountColumns, accountsList);
        renderTableSection('Customer Loans', loanColumns, loansList);
        renderTableSection('Customer Groups', groupColumns, groupsList);

        doc.save(`Customer_Inquiry_${nic || 'Summary'}.pdf`);
    };

    return (
        <div style={{ padding: 10 }}>
            <Card
                style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: '5px',
                    boxShadow: '0 2px 8px rgba(51, 39, 39, 0.1)',
                }}
            >
                {/* Header bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                        <h3 style={{ margin: 0 }}>
                            Customer Inquiry Summary {nic ? `- ${nic}` : ''}
                        </h3>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Input
                            placeholder="Enter NIC Number..."
                            value={searchNic}
                            onChange={(e) => setSearchNic(e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: 220 }}
                            allowClear
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        />
                        <Button
                            type="primary"
                            onClick={handleSearch}
                            disabled={loading || !searchNic.trim()}
                        >
                            {loading ? <Spin size="small" /> : 'Search'}
                        </Button>
                        {hasCustomerDetails && (
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={handleGeneratePDF}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                            >
                                Export PDF
                            </Button>
                        )}
                    </div>
                </div>

                <Spin spinning={loading}>
                    {!hasCustomerDetails ? (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <Empty
                                description={
                                    <span>
                                        Please enter a valid NIC number in the search box above to view customer details.
                                    </span>
                                }
                            />
                        </div>
                    ) : (
                        <>
                            {/* Customer Details Descriptions Section */}
                            <Descriptions
                                bordered
                                column={{ xxl: 4, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}
                                size="small"
                                style={{ marginBottom: 24 }}
                            >
                                <Descriptions.Item label="Customer ID">
                                    <strong>{customer.customerId || '-'}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    <strong>{customer.name || '-'}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="NIC Number">
                                    {customer.nic || nic || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Date of Birth">
                                    {formatDateOfBirth(customer.dateOfBirth)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Address" span={2}>
                                    {customer.address || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Mobile Number">
                                    {customer.mobileNumber || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Status">
                                    <Tag color={(customer.status || '').toUpperCase() === 'ACTIVE' ? 'success' : 'default'}>
                                        {customer.status || 'Active'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Type">
                                    {customer.customerType || 'Individual'}
                                </Descriptions.Item>
                            </Descriptions>

                            {/* Segmented Filter */}
                            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
                                <Segmented
                                    size="large"
                                    options={[
                                        { label: 'All Details', value: 'All' },
                                        { label: `Accounts (${accountsList.length})`, value: 'Accounts' },
                                        { label: `Loans (${loansList.length})`, value: 'Loans' },
                                        { label: `Groups (${groupsList.length})`, value: 'Groups' },
                                    ]}
                                    value={viewMode}
                                    onChange={setViewMode}
                                />
                            </div>

                            {/* Accounts Section */}
                            {(viewMode === 'All' || viewMode === 'Accounts') && (
                                <div style={{ marginBottom: 28 }}>
                                    <h4 style={{ marginBottom: 12, color: '#1f1f1f' }}>Customer Accounts</h4>
                                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Accounts</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                                    {accounts.totalAccounts ?? accountsList.length}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Account Balance</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                                                    {accounts.totalBalance !== undefined && accounts.totalBalance !== null
                                                        ? Number(accounts.totalBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        : '0.00'}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Available Balance</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#389e0d' }}>
                                                    {accounts.totalAvailableBalance !== undefined && accounts.totalAvailableBalance !== null
                                                        ? Number(accounts.totalAvailableBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        : '0.00'}
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Table
                                        columns={accountColumns}
                                        dataSource={accountsList}
                                        pagination={{
                                            pageSize: accountsPageSize,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['15', '30', '50', '100'],
                                            onShowSizeChange: (current, size) => setAccountsPageSize(size),
                                            locale: { items_per_page: '/ page' },
                                        }}
                                        bordered
                                        size="middle"
                                        rowKey={(record, index) => record.accountNumber || `acc-${index}`}
                                        rowClassName={(record, index) => index % 2 === 1 ? 'ant-table-row-alt' : ''}
                                        locale={{ emptyText: 'No accounts found for this customer' }}
                                    />
                                </div>
                            )}

                            {/* Loans Section */}
                            {(viewMode === 'All' || viewMode === 'Loans') && (
                                <div style={{ marginBottom: 28 }}>
                                    <h4 style={{ marginBottom: 12, color: '#1f1f1f' }}>Customer Loans</h4>
                                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                        <Col xs={24} sm={6}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Loans</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                                    {loans.totalLoans ?? loansList.length}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={6}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Approved Loan Amount</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                                    {loans.totalLoanAmount !== undefined && loans.totalLoanAmount !== null
                                                        ? Number(loans.totalLoanAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        : '0.00'}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={6}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Outstanding Balance</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#cf1322' }}>
                                                    {loans.totalOutstandingBalance !== undefined && loans.totalOutstandingBalance !== null
                                                        ? Number(loans.totalOutstandingBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        : '0.00'}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={6}>
                                            <Card size="small" type="inner">
                                                <Text type="secondary">Total Monthly Installment</Text>
                                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                                    {loans.totalInstallmentAmount !== undefined && loans.totalInstallmentAmount !== null
                                                        ? Number(loans.totalInstallmentAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        : '0.00'}
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Table
                                        columns={loanColumns}
                                        dataSource={loansList}
                                        pagination={{
                                            pageSize: loansPageSize,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['15', '30', '50', '100'],
                                            onShowSizeChange: (current, size) => setLoansPageSize(size),
                                            locale: { items_per_page: '/ page' },
                                        }}
                                        bordered
                                        size="middle"
                                        rowKey={(record, index) => record.loanNumber || `loan-${index}`}
                                        rowClassName={(record, index) => index % 2 === 1 ? 'ant-table-row-alt' : ''}
                                        locale={{ emptyText: 'No loans found for this customer' }}
                                    />
                                </div>
                            )}

                            {/* Groups Section */}
                            {(viewMode === 'All' || viewMode === 'Groups') && (
                                <div style={{ marginBottom: 12 }}>
                                    <h4 style={{ marginBottom: 12, color: '#1f1f1f' }}>Customer Groups</h4>
                                    <Table
                                        columns={groupColumns}
                                        dataSource={groupsList}
                                        pagination={{
                                            pageSize: groupsPageSize,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['15', '30', '50', '100'],
                                            onShowSizeChange: (current, size) => setGroupsPageSize(size),
                                            locale: { items_per_page: '/ page' },
                                        }}
                                        bordered
                                        size="middle"
                                        rowKey={(record, index) => record.groupId || `group-${index}`}
                                        rowClassName={(record, index) => index % 2 === 1 ? 'ant-table-row-alt' : ''}
                                        locale={{ emptyText: 'No group memberships found for this customer' }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Spin>
            </Card>
        </div>
    );
};

export default CustomerInquirySummaryTable;
